import { useId, useMemo, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { CalendarDay } from "./CalendarDay";

/**
 * DatePicker — small wrapper around shadcn Calendar + Popover with a
 * trigger styled to match the rest of the supplier portal's inline inputs.
 *
 * Props:
 *  - value:   "YYYY-MM-DD" string (controlled). Empty string = unset.
 *  - onChange:(yyyyMmDd: string) => void
 *  - minDate: optional Date — disables earlier dates
 *  - placeholder: trigger label when no date is selected
 *  - error:   error message (red border + message rendered below)
 *  - testId:  data-testid for the trigger button
 *
 * Value is exchanged as YYYY-MM-DD (the same wire format as the backend).
 */
export function DatePicker({ value, onChange, minDate, placeholder = "Select date", error, testId, ukFormat = false }) {
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const calendarId = testId || `date-picker-${generatedId}`;
  const DayCell = useMemo(() => function PickerDay(props) {
    return <CalendarDay {...props} testId={calendarId} />;
  }, [calendarId]);
  // Parse the YYYY-MM-DD string to a Date in LOCAL time to avoid the
  // off-by-one UTC shift that `new Date('2026-06-01')` produces in zones
  // west of UTC. Hours-min-sec at noon dodges DST transition cases too.
  const selected = value ? parseLocalDate(value) : undefined;
  const label = ukFormat && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split("-").reverse().join("/") : value;

  const handleSelect = (date) => {
    if (!date) return;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    onChange(`${yyyy}-${mm}-${dd}`);
    setOpen(false);
  };

  const triggerStyle = {
    width: "100%",
    padding: "0.4rem 0.6rem",
    border: error ? "1px solid #F050F8" : "1px solid rgba(96,240,248,0.15)",
    borderRadius: "4px",
    background: "rgba(10,18,42,0.6)",
    color: value ? "var(--octopus-ice)" : "rgba(96,240,248,0.6)",
    fontSize: "0.9rem",
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} style={triggerStyle}>
            <span>{label || placeholder}</span>
            <CalendarIcon size={14} style={{ opacity: 0.6, flexShrink: 0 }} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          data-testid={testId ? `${testId}-calendar` : undefined}
          align="start"
          style={{ padding: 0, background: "var(--octopus-deep)", border: "1px solid rgba(96,240,248,0.2)" }}
        >
          <Calendar
            mode="single"
            components={{ Day: DayCell }}
            selected={selected}
            defaultMonth={selected || minDate}
            onSelect={handleSelect}
            disabled={minDate ? { before: minDate } : undefined}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {error && <p data-testid={testId ? `${testId}-error` : undefined} style={{ color: "#F050F8", fontSize: "0.7rem", margin: "0.2rem 0 0" }}>{error}</p>}
    </>
  );
}

function parseLocalDate(yyyyMmDd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return undefined;
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d, 12, 0, 0);
}
